import { useCallback, useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'

import { routes } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { useEmailModalContext } from 'src/context/EmailModalContext'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import { GET_BOOKINGS } from 'src/graphql/bookings.graphql'
import { GET_TASKS, UPDATE_TASK } from 'src/graphql/tasks.graphql'
import { BookingStatus } from 'src/graphql/types/bookings'
import { TaskStatus } from 'src/graphql/types/tasks'
import { buildFullName } from 'src/lib/formatters'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import { unformatPhoneNumber } from 'src/lib/phone.utils'

import type { AgendaItem } from '../lib/agendaItems'
import { buildAgendaGroups } from '../lib/agendaItems'

import AgendaWidgetView from './AgendaWidgetView'

const BOOKINGS_LIMIT = 50
const TASKS_LIMIT = 100
// Only today's completions are shown, so this needs far less headroom.
const COMPLETED_TASKS_LIMIT = 50

/**
 * Data container for the agenda widget. Owns the two queries, the merge, and
 * every side effect (drawer, phone device, email modal, task completion) so
 * `AgendaWidgetView` can stay pure.
 */
export interface AgendaWidgetProps {
  /**
   * Whose work to show. Defaults to the signed-in employee alone.
   *
   * `null` means no owner clause at all — the whole company, including the
   * bookings and tasks that belong to nobody. That is not a special case
   * invented here: `buildFiltersQuery` pushes no clause for an empty array, so
   * passing nothing through is exactly what the services already do.
   *
   * Every one of these queries is company-scoped server-side by
   * `context.currentUser.companyId`, so widening the scope grants no access the
   * viewer did not already have.
   */
  employeeIds?: string[] | null
}

const AgendaWidget = ({ employeeIds }: AgendaWidgetProps) => {
  const { currentUser } = useAuth()
  // `active.employeeId` is the current structure; `employeeId` is the
  // deprecated flat field still present on older tokens.
  const currentEmployeeId =
    currentUser?.active?.employeeId ?? currentUser?.employeeId

  // `undefined` means the caller said nothing, so fall back to just me; `null`
  // is a deliberate "everyone" and must survive.
  const scopeIds =
    employeeIds === undefined
      ? currentEmployeeId
        ? [currentEmployeeId]
        : []
      : employeeIds ?? []

  // Only a missing signed-in employee should stop the queries. An empty scope
  // array is the company-wide case and must be allowed through.
  const canQuery = Boolean(currentEmployeeId)

  // Rows name their owner unless the scope is exactly one person — with one
  // person it would be the same name on every row, saying nothing the control
  // does not already say.
  const showOwner = scopeIds.length !== 1

  const applicantDrawer = useApplicantDrawerContext()
  const taskModal = useTaskModalContext()
  const emailModal = useEmailModalContext()
  const dispatchPhoneAction = usePhoneDeviceDispatch()
  const phoneDeviceContext = usePhoneDevice()

  // Offset in days from today, rather than a date: it survives the clock ticking
  // past midnight without stranding the user on a stale "today".
  const [dayOffset, setDayOffset] = useState(0)

  // One clock for the whole render pass: the group split, the "has it ended"
  // dimming and the now marker must all agree.
  const { now, selectedDate, viewingToday, startOfDay, endOfDay } =
    useMemo(() => {
      const today = dayjs()
      const day = today.add(dayOffset, 'day')
      return {
        now: today.toDate(),
        selectedDate: day.toDate(),
        viewingToday: dayOffset === 0,
        startOfDay: day.startOf('day').toISOString(),
        endOfDay: day.endOf('day').toISOString(),
      }
    }, [dayOffset])

  const onBookingsError = useCallback(() => {
    toast('Unable to load your appointments, please try again.', 'error')
  }, [])

  const onTasksError = useCallback(() => {
    toast('Unable to load your tasks, please try again.', 'error')
  }, [])

  const { data: bookingsData, loading: bookingsLoading } = useQuery(
    GET_BOOKINGS,
    {
      variables: {
        filters: {
          // Cancelled and no-show bookings are fetched on purpose: the agenda
          // strikes them through rather than letting an appointment the voice
          // agent made silently vanish.
          bookingStatuses: [
            BookingStatus.ACCEPTED,
            BookingStatus.CANCELLED,
            BookingStatus.NO_SHOW,
          ],
          recruiters: scopeIds,
          // `bookingsFilters` only applies the range when both ends are set.
          startDate: startOfDay,
          endDate: endOfDay,
        },
        pageInfo: { offset: 0, limit: BOOKINGS_LIMIT },
      },
      skip: !canQuery,
      onError: onBookingsError,
      notifyOnNetworkStatusChange: true,
    }
  )

  const {
    data: tasksData,
    loading: tasksLoading,
    refetch: refetchTasks,
  } = useQuery(GET_TASKS, {
    variables: {
      filters: {
        status: [TaskStatus.NOT_STARTED, TaskStatus.IN_PROGRESS],
        assigneeIds: scopeIds,
        // On today, everything due up to tonight — which pulls in the overdue
        // carryover the widget groups separately. On any other day, only that
        // day, since carryover is meaningless there.
        dueDate: viewingToday
          ? { lte: endOfDay }
          : { gte: startOfDay, lte: endOfDay },
      },
      pageInfo: { offset: 0, limit: TASKS_LIMIT },
    },
    skip: !canQuery,
    onError: onTasksError,
    notifyOnNetworkStatusChange: true,
  })

  /**
   * Completed tasks stay on the agenda, greyed and struck, rather than vanishing
   * the moment you tick them — disappearing rows make it hard to tell what you
   * actually got through.
   *
   * Deliberately a second query rather than adding COMPLETED to the one above:
   * that query reaches back over every overdue day, so completed work would
   * accumulate without bound and crowd open tasks out of the page limit. This
   * one is fenced to today only.
   */
  const {
    data: completedTasksData,
    loading: completedTasksLoading,
    refetch: refetchCompletedTasks,
  } = useQuery(GET_TASKS, {
    variables: {
      filters: {
        status: [TaskStatus.COMPLETED],
        assigneeIds: scopeIds,
        dueDate: { gte: startOfDay, lte: endOfDay },
      },
      pageInfo: { offset: 0, limit: COMPLETED_TASKS_LIMIT },
    },
    skip: !canQuery,
    onError: onTasksError,
    notifyOnNetworkStatusChange: true,
  })

  // Creating a task from the widget's "+" should show up without a reload.
  useEffect(() => {
    taskModal?.registerRefetch?.(() => {
      refetchTasks()
      refetchCompletedTasks()
    })
  }, [taskModal, refetchTasks, refetchCompletedTasks])

  // Memoized so the `?? []` fallback doesn't hand the memos below a fresh array
  // identity on every render.
  const bookings = useMemo(
    () => bookingsData?.getBookings?.items ?? [],
    [bookingsData]
  )
  // Open tasks plus today's completions, merged before the domain layer sees
  // them so grouping and ordering treat them identically.
  const tasks = useMemo(
    () => [
      ...(tasksData?.tasks?.items ?? []),
      ...(completedTasksData?.tasks?.items ?? []),
    ],
    [tasksData, completedTasksData]
  )

  /**
   * How much the page limits are hiding.
   *
   * A company-wide day can exceed both limits — bookings come back ascending and
   * the open-task query reaches over every overdue day, so what drops off is the
   * later appointments and the newer tasks. Silently showing the first N of a
   * manager's team view is exactly the kind of quiet lie this control exists to
   * remove, so the widget says how many it is holding back.
   *
   * Completed tasks are deliberately not counted: they are already done, so
   * truncating them hides nothing anyone still has to act on.
   */
  const hiddenCount = useMemo(() => {
    const bookingsTotal = bookingsData?.getBookings?.totalCount ?? 0
    const openTasks = tasksData?.tasks?.items ?? []
    const tasksTotal = tasksData?.tasks?.totalCount ?? 0
    return (
      Math.max(0, bookingsTotal - bookings.length) +
      Math.max(0, tasksTotal - openTasks.length)
    )
  }, [bookingsData, tasksData, bookings])

  const groups = useMemo(
    () =>
      buildAgendaGroups({
        bookings: bookings.map((booking) => ({
          ...booking,
          // The domain layer wants a flat agent name; GraphQL nests it under
          // the booking's originating activity log.
          voiceAgentName: booking.activityLog?.voiceAgent?.name ?? null,
        })),
        tasks,
        now,
        selectedDate,
      }),
    [bookings, tasks, now, selectedDate]
  )

  // The row only carries ids, but placing a call needs the phone number, so
  // keep a lookup back to the raw records.
  const applicantByItemId = useMemo(() => {
    const map = new Map<string, { phone?: string | null; name: string }>()
    bookings.forEach((booking) => {
      if (booking.applicant) {
        map.set(`booking:${booking.bookingId}`, {
          phone: booking.applicant.phone,
          name: buildFullName(booking.applicant),
        })
      }
    })
    tasks.forEach((task) => {
      if (task.applicant) {
        map.set(`task:${task.taskId}`, {
          phone: task.applicant.phone,
          name: buildFullName(task.applicant),
        })
      }
    })
    return map
  }, [bookings, tasks])

  const handleOpenApplicant = useCallback(
    (applicantId: string, { newTab }: { newTab: boolean }) => {
      // Meta-key opens a real tab; a plain click keeps the user in place with
      // the drawer. Same idiom as BookingCard.
      if (newTab) {
        window.open(
          routes.applicantDetails({ id: applicantId, tab: 'application' }),
          '_blank'
        )
        return
      }
      applicantDrawer?.handleOpen(applicantId)
    },
    [applicantDrawer]
  )

  const handleAction = useCallback(
    (item: AgendaItem) => {
      if (!item.applicantId) return

      if (item.action === 'EMAIL') {
        emailModal?.handleOpen?.({ applicantId: item.applicantId })
        return
      }

      if (item.action === 'CALL') {
        if (phoneDeviceContext?.phoneDevice?.isBusy) return
        const applicant = applicantByItemId.get(item.id)
        if (!applicant?.phone) {
          toast('No phone number on file for this applicant.', 'error')
          return
        }
        dispatchPhoneAction?.({
          type: PhoneDeviceActionType.CALL,
          payload: {
            applicantId: item.applicantId,
            phone: unformatPhoneNumber(applicant.phone),
            applicantFullName: applicant.name,
          },
        })
      }
    },
    [applicantByItemId, dispatchPhoneAction, emailModal, phoneDeviceContext]
  )

  const [updateTask] = useMutation(UPDATE_TASK)

  const handleCompleteTask = useCallback(
    async (item: AgendaItem) => {
      if (!item.taskId) return
      try {
        await updateTask({
          variables: {
            taskId: item.taskId,
            input: { status: TaskStatus.COMPLETED },
          },
        })
        // Both lists: the task leaves the open query and joins the completed
        // one, so it stays on the agenda greyed out instead of vanishing.
        await Promise.all([refetchTasks(), refetchCompletedTasks()])
      } catch (error) {
        toast('Error updating task', 'error')
        // Rethrown so the row rolls its optimistic tick back.
        throw error
      }
    },
    [refetchTasks, refetchCompletedTasks, updateTask]
  )

  const handleOpenTask = useCallback(
    (item: AgendaItem) => {
      if (!item.taskId) return
      // Same modal the Tasks page and the old dashboard rows open, so edits
      // land in one place. `id` loads the existing task.
      taskModal?.handleOpen?.({ id: item.taskId })
    },
    [taskModal]
  )

  return (
    <AgendaWidgetView
      groups={groups}
      now={now}
      loading={bookingsLoading || tasksLoading || completedTasksLoading}
      onAddTask={() => taskModal?.handleOpen?.()}
      onOpenApplicant={handleOpenApplicant}
      onAction={handleAction}
      onCompleteTask={handleCompleteTask}
      onOpenTask={handleOpenTask}
      selectedDate={selectedDate}
      onChangeDay={(delta) => setDayOffset((prev) => prev + delta)}
      onGoToToday={() => setDayOffset(0)}
      showOwner={showOwner}
      currentEmployeeId={currentEmployeeId}
      hiddenCount={hiddenCount}
    />
  )
}

export default AgendaWidget
